import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, notification } from 'antd';
import useEnv from '@shared/useEnv';
import Button, { ButtonSize } from '@components/shared/V2Button';
import api from '@shared/api';
import { ValidIconTypes } from '@components/shared/CustomIcon';

/* eslint-disable camelcase */
export type DefaultCloudinaryResponseType = {
  public_id: string;
  original_filename: string;
  format: string;
  created_at: string;
};
/* eslint-enable camelcase */

export type DefaultVideoUploadType = {
  cloudinaryId: string;
  filename: string;
  processedAt: string;
};

type Props<CloudinaryResponseType, VideoUploadType> = {
  marketplaceRequest: (
    file: VideoUploadType | DefaultVideoUploadType,
  ) => PromiseLike<{ ok: boolean }>;
  onSuccess: () => void;
  cloudinaryTransformer?: (
    json: CloudinaryResponseType | DefaultCloudinaryResponseType,
  ) => VideoUploadType | DefaultVideoUploadType;
  buttonIcon?: ValidIconTypes;
  buttonSize?: ButtonSize;
  buttonClass?: string;
};

const pluckFileAttributesFromCloudinary = (
  cloudinaryJson: DefaultCloudinaryResponseType,
): DefaultVideoUploadType => {
  /* eslint-disable camelcase */
  const { public_id, original_filename, format, created_at } = cloudinaryJson;
  return {
    cloudinaryId: public_id,
    filename: `${original_filename}.${format}`,
    processedAt: created_at,
  };
  /* eslint-enable camelcase */
};

const VideoUploader = <CloudinaryResponseType, VideoUploadType>({
  onSuccess,
  marketplaceRequest,
  // @ts-expect-error
  cloudinaryTransformer = pluckFileAttributesFromCloudinary,
  buttonIcon = undefined,
  buttonSize = undefined,
  buttonClass = '',
}: Props<CloudinaryResponseType, VideoUploadType>) => {
  const { t } = useTranslation('admin');
  const { cloudinaryCloud, cloudinaryUploadPreset } = useEnv();
  const [isUploading, setIsUploading] = useState(false);
  const [percentUploaded, setPercentUploaded] = useState(0);

  const resetUploadState = () => {
    setIsUploading(false);
    setPercentUploaded(0);
  };

  const uploadToMarketplace = async uploadedFile => {
    const marketplaceResponse = await marketplaceRequest(uploadedFile);
    resetUploadState();
    if (marketplaceResponse.ok) {
      onSuccess();
      notification.success({
        message: t('media.videoUploadSuccess'),
      });
    } else {
      notification.error({
        message: t('media.videoUploadFailure'),
      });
    }
  };

  const noop = () => {};

  const getSlice = (file, start, end) => {
    let slice = noop;

    if (file.mozSlice) {
      slice = file.mozSlice;
    } else if (file.webkitSlice) {
      slice = file.webkitSlice;
    } else if (file.slice) {
      slice = file.slice;
    }

    // @ts-expect-error
    return slice.bind(file)(start, end);
  };

  const send = (piece, start, end, size, uploadId, name) => {
    const formData = new FormData();
    formData.append('file', piece, name);
    formData.append('upload_preset', cloudinaryUploadPreset || '');

    return api.fetch(`https://api.cloudinary.com/v1_1/${cloudinaryCloud}/auto/upload`, {
      method: 'POST',
      body: formData,
      headers: {
        'X-Unique-Upload-Id': uploadId,
        'Content-Range': `bytes ${start}-${end}/${size}`,
      },
    });
  };

  const uploadVideo = upload => {
    const sliceSize = 6000000;
    const { file } = upload;
    const { size } = file;
    let start = 0;
    const XUniqueUploadId = +new Date();

    const loop = async () => {
      let end = start + sliceSize;

      if (end > size) {
        end = size;
      }
      const slice = getSlice(file, start, end);

      const response = await send(slice, start, end - 1, size, XUniqueUploadId, file.name);
      const cloudinaryJson = await response.json();
      setPercentUploaded(Math.trunc((end / size) * 100));

      if (end < size) {
        start += sliceSize;
        setTimeout(loop, 3);
      } else if (response.ok) {
        uploadToMarketplace(cloudinaryTransformer(cloudinaryJson));
      } else {
        resetUploadState();
        notification.error({
          message: t('media.videoUploadFailure'),
        });
      }
    };

    setTimeout(loop, 3);
  };

  const beforeVideoUpload = async () => {
    setIsUploading(true);
  };

  return (
    <Upload
      accept=".mov,.mp4,.mpg,.avi,.wmv,.webm,.m4v"
      beforeUpload={beforeVideoUpload}
      customRequest={uploadVideo}
      showUploadList={false}
    >
      <Button loading={isUploading} icon={buttonIcon} size={buttonSize} className={buttonClass}>
        {isUploading
          ? t('media.uploadProgress', { percentage: percentUploaded })
          : t('media.uploadVideo')}
      </Button>
    </Upload>
  );
};

export default VideoUploader;
